import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, ListMusic, Mic2, Music4, RefreshCw, Save, ShieldBan, Trash2, Upload } from 'lucide-react';
import { SectionCard } from '../Scheduler/utils';

const liveMusicFetch = async (path, options = {}) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const normalizedPath = path.startsWith('/api/') ? path : `/api/live-music${path}`;
  const res = await fetch(normalizedPath, {
    cache: 'no-store',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    let message = 'Live music request failed';
    try {
      const payload = await res.json();
      message = payload?.message || message;
    } catch (_error) {
      const text = await res.text();
      if (text) message = text;
    }
    throw new Error(message);
  }

  return res.json();
};

const createEmptySongForm = () => ({
  id: null,
  title: '',
  artistName: '',
  albumName: '',
  genre: '',
  durationSeconds: '',
  notes: '',
});

const createEmptySetForm = () => ({
  id: null,
  name: '',
  description: '',
  isActive: true,
  songs: [],
});

const toSetSongDraft = (item, fallbackPosition = 1) => ({
  songId: Number(item.songId),
  positionValue: Number(item.positionValue || fallbackPosition),
  nameThatTuneEnabled: item.nameThatTuneEnabled !== false,
  songRequestsEnabled: item.songRequestsEnabled !== false,
  songSinBinEnabled: !!item.songSinBinEnabled,
  notes: item.notes || '',
});

const buildSetFormFromItem = (item) => ({
  id: Number(item.id),
  name: item.name || '',
  description: item.description || '',
  isActive: item.isActive !== false,
  songs: Array.isArray(item.songs)
    ? item.songs.map((song, index) => toSetSongDraft(song, index + 1))
    : [],
});

export default function LiveMusicLibraryPanel({ siteId, stageLocked, onLocked }) {
  const songImportInputRef = useRef(null);
  const [songs, setSongs] = useState([]);
  const [sets, setSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState(null);
  const [selectedSetSiteId, setSelectedSetSiteId] = useState(null);
  const [setEditor, setSetEditor] = useState(createEmptySetForm());
  const [songForm, setSongForm] = useState(createEmptySongForm());
  const [songSearch, setSongSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [songImportText, setSongImportText] = useState('');
  const [songImportSources, setSongImportSources] = useState([]);
  const [songImportJob, setSongImportJob] = useState(null);
  const [songImporting, setSongImporting] = useState(false);
  const [songImportMessage, setSongImportMessage] = useState('');
  const [songImportError, setSongImportError] = useState('');

  const filteredSongs = useMemo(() => {
    const q = songSearch.trim().toLowerCase();
    if (!q) return songs;
    return songs.filter((song) => {
      const hay = `${song.title || ''} ${song.artistName || ''} ${song.albumName || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [songs, songSearch]);

  const assignedSongIds = useMemo(
    () => new Set((setEditor.songs || []).map((song) => Number(song.songId)).filter(Boolean)),
    [setEditor.songs]
  );

  const appendSongImportSources = (entries = []) => {
    setSongImportSources((current) => {
      const next = [...current];
      for (const entry of entries) {
        if (!entry?.text) continue;
        if (next.length >= 12) break;
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title: entry.title || entry.filename || `Song import ${next.length + 1}`,
          filename: entry.filename || '',
          mimeType: entry.mimeType || '',
          text: entry.text,
        });
      }
      return next;
    });
  };

  const refreshSongs = async () => {
    if (!siteId) return;
    const res = await liveMusicFetch(`/songs?siteId=${encodeURIComponent(siteId)}`);
    setSongs(Array.isArray(res.items) ? res.items : []);
  };

  const refreshSets = async (preferredSetId = null) => {
    if (!siteId) return;
    const res = await liveMusicFetch(`/sets?siteId=${encodeURIComponent(siteId)}`);
    const items = Array.isArray(res.items) ? res.items : [];
    setSets(items);
    setSelectedSetId((current) => {
      if (preferredSetId && items.find((item) => Number(item.id) === Number(preferredSetId))) {
        setSelectedSetSiteId(Number(siteId));
        return Number(preferredSetId);
      }
      if (current && items.find((item) => Number(item.id) === Number(current))) {
        setSelectedSetSiteId(Number(siteId));
        return current;
      }
      setSelectedSetSiteId(items[0] ? Number(siteId) : null);
      return items[0] ? Number(items[0].id) : null;
    });
    if (!items.length) {
      setSetEditor(createEmptySetForm());
    }
  };

  const refreshAll = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      await Promise.all([refreshSongs(), refreshSets(null)]);
    } catch (error) {
      setMessage(error.message || 'Failed to load live music setup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSongForm(createEmptySongForm());
    setSetEditor(createEmptySetForm());
    setSelectedSetId(null);
    setSelectedSetSiteId(null);
    setSongImportText('');
    setSongImportSources([]);
    setSongImportJob(null);
    setSongImporting(false);
    setSongImportMessage('');
    setSongImportError('');
    if (!siteId) {
      setSongs([]);
      setSets([]);
      return;
    }
    refreshAll();
  }, [siteId]);

  useEffect(() => {
    if (!siteId || !selectedSetId || Number(selectedSetSiteId) !== Number(siteId)) return;
    let active = true;
    setDetailLoading(true);
    liveMusicFetch(`/sets/${selectedSetId}?siteId=${encodeURIComponent(siteId)}`)
      .then((res) => {
        if (!active) return;
        if (res?.item) {
          setSetEditor(buildSetFormFromItem(res.item));
        }
      })
      .catch((error) => {
        if (!active) return;
        setMessage(error.message || 'Failed to load set detail');
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedSetId, selectedSetSiteId, siteId]);

  useEffect(() => {
    if (!songImportJob?.id || !siteId) return undefined;

    let cancelled = false;
    let timerId = null;

    const pollJob = async () => {
      try {
        const data = await liveMusicFetch(
          `/imports/${encodeURIComponent(songImportJob.id)}?siteId=${encodeURIComponent(siteId)}`
        );
        if (cancelled) return;
        setSongImportJob(data);
        if (data?.status === 'completed') {
          const summary = data?.summary && typeof data.summary === 'object' ? data.summary : {};
          const imported = summary?.imported && typeof summary.imported === 'object' ? summary.imported : {};
          const warningsNote = Number(data?.progress?.warningCount || 0) > 0
            ? ` ${Number(data.progress.warningCount)} warning${Number(data.progress.warningCount) === 1 ? '' : 's'} logged.`
            : '';
          setSongImporting(false);
          setSongImportMessage(
            `${data?.message || 'Song import complete.'} ${Number(imported.created || 0)} created, ${Number(imported.updated || 0)} updated.${warningsNote}`.trim()
          );
          setSongImportError('');
          setSongImportSources([]);
          setSongImportText('');
          await refreshSongs();
          await refreshSets(selectedSetId);
          return;
        }
        if (data?.status === 'failed') {
          setSongImporting(false);
          setSongImportError(data?.error || data?.message || 'Gordon failed to import the song sources.');
          return;
        }

        setSongImporting(true);
        setSongImportMessage(data?.message || 'Gordon is processing the song sources.');
        timerId = window.setTimeout(pollJob, 1500);
      } catch (error) {
        if (cancelled) return;
        setSongImporting(false);
        setSongImportError(error.message || 'Failed to track song import progress.');
      }
    };

    pollJob();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [selectedSetId, siteId, songImportJob?.id]);

  const ensureWritable = () => {
    if (!stageLocked) return true;
    onLocked?.();
    return false;
  };

  const handleSongImportFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      setSongImportError('');
      const entries = await Promise.all(
        files.slice(0, Math.max(0, 12 - songImportSources.length)).map(async (file) => ({
          title: file.name,
          filename: file.name,
          mimeType: file.type || 'text/plain',
          text: await file.text(),
        }))
      );
      appendSongImportSources(entries);
      setSongImportMessage(
        `${entries.length} text source${entries.length === 1 ? '' : 's'} ready for AI import.`
      );
    } catch (error) {
      setSongImportError(error.message || 'Failed to read selected files.');
    } finally {
      if (songImportInputRef.current) {
        songImportInputRef.current.value = '';
      }
    }
  };

  const addPastedSongImportSource = () => {
    const text = songImportText.trim();
    if (!text) return;
    appendSongImportSources([{
      title: `Pasted source ${songImportSources.length + 1}`,
      filename: '',
      mimeType: 'text/plain',
      text,
    }]);
    setSongImportText('');
    setSongImportError('');
    setSongImportMessage('Pasted text added to the AI import queue.');
  };

  const removeSongImportSource = (sourceId) => {
    setSongImportSources((current) => current.filter((entry) => entry.id !== sourceId));
  };

  const clearSongImportSources = () => {
    setSongImportText('');
    setSongImportSources([]);
    setSongImportJob(null);
    setSongImporting(false);
    setSongImportMessage('');
    setSongImportError('');
    if (songImportInputRef.current) {
      songImportInputRef.current.value = '';
    }
  };

  const startSongImport = async () => {
    if (!siteId || !ensureWritable() || !songImportSources.length) return;
    try {
      setSongImporting(true);
      setSongImportError('');
      setSongImportMessage('');
      setSongImportJob(null);
      const res = await liveMusicFetch('/imports', {
        method: 'POST',
        body: JSON.stringify({
          siteId,
          textEntries: songImportSources.map((entry) => ({
            title: entry.title,
            filename: entry.filename,
            mimeType: entry.mimeType,
            text: entry.text,
          })),
        }),
      });
      if (!res?.job?.id) {
        throw new Error('Song import started but no job id was returned.');
      }
      setSongImportJob(res.job);
      setSongImportMessage(res.job.message || 'Gordon has started processing the song sources.');
    } catch (error) {
      setSongImporting(false);
      setSongImportError(error.message || 'Failed to start song import.');
    }
  };

  const saveSong = async () => {
    if (!siteId || !ensureWritable()) return;
    try {
      setMessage('');
      const payload = {
        siteId,
        title: songForm.title,
        artistName: songForm.artistName,
        albumName: songForm.albumName,
        genre: songForm.genre,
        durationSeconds: songForm.durationSeconds ? Number(songForm.durationSeconds) : null,
        notes: songForm.notes,
      };
      if (songForm.id) {
        await liveMusicFetch(`/songs/${songForm.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await liveMusicFetch('/songs', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setSongForm(createEmptySongForm());
      await refreshSongs();
      if (selectedSetId) {
        await refreshSets(selectedSetId);
      }
      setMessage(songForm.id ? 'Song updated.' : 'Song added.');
    } catch (error) {
      setMessage(error.message || 'Failed to save song');
    }
  };

  const deleteSong = async (songId) => {
    if (!siteId || !ensureWritable()) return;
    if (!window.confirm('Delete this song from the library?')) return;
    try {
      await liveMusicFetch(`/songs/${songId}?siteId=${encodeURIComponent(siteId)}`, {
        method: 'DELETE',
      });
      await refreshSongs();
      await refreshSets(selectedSetId);
      if (selectedSetId) {
        const res = await liveMusicFetch(`/sets/${selectedSetId}?siteId=${encodeURIComponent(siteId)}`);
        setSetEditor(buildSetFormFromItem(res.item));
      }
      setMessage('Song deleted.');
    } catch (error) {
      setMessage(error.message || 'Failed to delete song');
    }
  };

  const saveSet = async () => {
    if (!siteId || !ensureWritable()) return;
    try {
      setMessage('');
      const payload = {
        siteId,
        name: setEditor.name,
        description: setEditor.description,
        isActive: setEditor.isActive,
        songs: (setEditor.songs || []).map((song, index) => ({
          ...song,
          positionValue: Number(song.positionValue || index + 1),
        })),
      };
      let result = null;
      if (setEditor.id) {
        result = await liveMusicFetch(`/sets/${setEditor.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        result = await liveMusicFetch('/sets', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      const nextId = result?.item?.id || setEditor.id;
      await refreshSets(nextId);
      if (nextId) {
        const detail = await liveMusicFetch(`/sets/${nextId}?siteId=${encodeURIComponent(siteId)}`);
        setSetEditor(buildSetFormFromItem(detail.item));
        setSelectedSetId(Number(nextId));
      } else {
        setSetEditor(createEmptySetForm());
      }
      setMessage(setEditor.id ? 'Set updated.' : 'Set created.');
    } catch (error) {
      setMessage(error.message || 'Failed to save set');
    }
  };

  const deleteSet = async (setId) => {
    if (!siteId || !ensureWritable()) return;
    if (!window.confirm('Delete this live music set?')) return;
    try {
      await liveMusicFetch(`/sets/${setId}?siteId=${encodeURIComponent(siteId)}`, {
        method: 'DELETE',
      });
      await refreshSets(null);
      setMessage('Set deleted.');
    } catch (error) {
      setMessage(error.message || 'Failed to delete set');
    }
  };

  const toggleSongInSet = (songId, checked) => {
    setSetEditor((current) => {
      const existingSongs = Array.isArray(current.songs) ? current.songs : [];
      if (checked) {
        if (existingSongs.find((song) => Number(song.songId) === Number(songId))) {
          return current;
        }
        return {
          ...current,
          songs: [
            ...existingSongs,
            {
              songId: Number(songId),
              positionValue: existingSongs.length + 1,
              nameThatTuneEnabled: true,
              songRequestsEnabled: true,
              songSinBinEnabled: false,
              notes: '',
            },
          ],
        };
      }
      return {
        ...current,
        songs: existingSongs
          .filter((song) => Number(song.songId) !== Number(songId))
          .map((song, index) => ({ ...song, positionValue: index + 1 })),
      };
    });
  };

  const updateSetSong = (songId, patch) => {
    setSetEditor((current) => ({
      ...current,
      songs: (current.songs || []).map((song) =>
        Number(song.songId) === Number(songId) ? { ...song, ...patch } : song
      ),
    }));
  };

  const selectedSongRows = useMemo(
    () =>
      (setEditor.songs || [])
        .map((assigned) => ({
          assigned,
          song: songs.find((song) => Number(song.id) === Number(assigned.songId)) || null,
        }))
        .filter((row) => row.song)
        .sort((a, b) => Number(a.assigned.positionValue || 0) - Number(b.assigned.positionValue || 0)),
    [setEditor.songs, songs]
  );

  return (
    <SectionCard title="Live Music Setup" icon={Music4}>
      <div className="grid gap-4">
        <div className="text-sm text-slate-300">
          Build the reusable song library for <code className="text-slate-100">Live Music Experience</code>, then arrange songs into show sets and mark which experiences each song can appear in.
        </div>

        {!siteId ? (
          <div className="text-sm text-slate-400">Select a site to manage its live music song library.</div>
        ) : (
          <>
            <div className="grid xl:grid-cols-[1.1fr,1fr] gap-4">
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">Song Library</div>
                    <div className="text-xs text-slate-400">Add tracks manually, or import flexible text sources through Gordon.</div>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs bg-white/10 hover:bg-white/15"
                    onClick={refreshAll}
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </button>
                </div>

                <div className="rounded-2xl bg-slate-950/35 ring-1 ring-white/10 p-3 grid gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">AI Song Import</div>
                      <div className="text-xs text-slate-400">
                        Upload text files or paste raw set lists, CSV rows, lyric sheets, or copied spreadsheet content. Gordon will normalize them into the song library.
                      </div>
                    </div>
                    <input
                      ref={songImportInputRef}
                      type="file"
                      accept=".txt,.csv,.json,.md,.text"
                      multiple
                      className="hidden"
                      onChange={handleSongImportFileChange}
                    />
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs bg-white/10 hover:bg-white/15"
                      onClick={() => songImportInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4" /> Add Files
                    </button>
                  </div>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Paste source text</span>
                    <textarea
                      rows={5}
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songImportText}
                      onChange={(e) => setSongImportText(e.target.value)}
                      placeholder="Paste a set list, CSV rows, copied spreadsheet data, or lyrics sheet here…"
                    />
                  </label>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs bg-white/10 hover:bg-white/15"
                      onClick={addPastedSongImportSource}
                      disabled={!songImportText.trim()}
                    >
                      <FileText className="w-4 h-4" /> Add Pasted Source
                    </button>
                    <button
                      type="button"
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
                        stageLocked ? 'bg-slate-700/60 text-slate-300 cursor-not-allowed' : 'bg-sky-600/90 hover:bg-sky-600'
                      }`}
                      onClick={startSongImport}
                      disabled={stageLocked || songImporting || !songImportSources.length}
                    >
                      <Upload className="w-4 h-4" /> {songImporting ? 'Importing…' : 'Import With AI'}
                    </button>
                    {(songImportSources.length || songImportText || songImportJob?.id) ? (
                      <button
                        type="button"
                        className="rounded-xl px-3 py-2 text-xs bg-white/10 hover:bg-white/15"
                        onClick={clearSongImportSources}
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>

                  <div className="grid gap-2">
                    {songImportSources.length ? songImportSources.map((entry, index) => (
                      <div
                        key={entry.id}
                        className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-3 py-2 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white truncate">{entry.title || entry.filename || `Source ${index + 1}`}</div>
                          <div className="text-[11px] text-slate-400">
                            {[entry.filename, entry.mimeType, `${entry.text.length} chars`].filter(Boolean).join(' • ')}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="p-2 rounded-xl bg-white/10 hover:bg-rose-500/30"
                          onClick={() => removeSongImportSource(entry.id)}
                          title="Remove source"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )) : (
                      <div className="text-xs text-slate-500">No AI import sources queued yet.</div>
                    )}
                  </div>

                  {songImporting && songImportJob ? (
                    <div className="rounded-2xl bg-sky-500/10 ring-1 ring-sky-400/20 px-3 py-2 text-xs text-sky-100">
                      <div className="font-medium">{songImportJob.message || 'Gordon is processing the song sources.'}</div>
                      <div className="text-sky-100/80">
                        {songImportJob.stage || 'Working'}
                        {songImportJob?.progress?.totalSources
                          ? ` · ${Number(songImportJob.progress.completedSources || 0)} of ${Number(songImportJob.progress.totalSources || 0)} complete`
                          : ''}
                        {songImportJob?.progress?.warningCount
                          ? ` · ${Number(songImportJob.progress.warningCount)} warning${Number(songImportJob.progress.warningCount) === 1 ? '' : 's'}`
                          : ''}
                      </div>
                      {songImportJob.currentSourceLabel ? (
                        <div className="text-sky-100/80">Current source: {songImportJob.currentSourceLabel}</div>
                      ) : null}
                    </div>
                  ) : null}

                  {songImportError ? (
                    <div className="rounded-xl bg-rose-500/10 ring-1 ring-rose-400/20 px-3 py-2 text-xs text-rose-100">
                      {songImportError}
                    </div>
                  ) : null}

                  {songImportMessage ? (
                    <div className="rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/20 px-3 py-2 text-xs text-emerald-100">
                      {songImportMessage}
                    </div>
                  ) : null}
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Title</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songForm.title}
                      onChange={(e) => setSongForm({ ...songForm, title: e.target.value })}
                    />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Artist</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songForm.artistName}
                      onChange={(e) => setSongForm({ ...songForm, artistName: e.target.value })}
                    />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Album</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songForm.albumName}
                      onChange={(e) => setSongForm({ ...songForm, albumName: e.target.value })}
                    />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Genre</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songForm.genre}
                      onChange={(e) => setSongForm({ ...songForm, genre: e.target.value })}
                    />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Duration (seconds)</span>
                    <input
                      type="number"
                      min={1}
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songForm.durationSeconds}
                      onChange={(e) => setSongForm({ ...songForm, durationSeconds: e.target.value })}
                    />
                  </label>
                  <label className="grid gap-1 text-sm sm:col-span-2">
                    <span className="text-xs text-slate-300">Notes</span>
                    <textarea
                      rows={3}
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={songForm.notes}
                      onChange={(e) => setSongForm({ ...songForm, notes: e.target.value })}
                    />
                  </label>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
                      stageLocked ? 'bg-slate-700/60 text-slate-300 cursor-not-allowed' : 'bg-indigo-600/90 hover:bg-indigo-600'
                    }`}
                    onClick={saveSong}
                    disabled={stageLocked}
                  >
                    <Save className="w-4 h-4" /> {songForm.id ? 'Update Song' : 'Add Song'}
                  </button>
                  {songForm.id ? (
                    <button
                      type="button"
                      className="rounded-xl px-3 py-2 text-xs bg-white/10 hover:bg-white/15"
                      onClick={() => setSongForm(createEmptySongForm())}
                    >
                      Cancel Edit
                    </button>
                  ) : null}
                </div>

                <input
                  className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-sm text-slate-100"
                  placeholder="Search songs in this site library…"
                  value={songSearch}
                  onChange={(e) => setSongSearch(e.target.value)}
                />

                <div className="grid gap-2 max-h-[420px] overflow-auto">
                  {loading ? (
                    <div className="text-sm text-slate-400">Loading songs…</div>
                  ) : filteredSongs.length ? (
                    filteredSongs.map((song) => (
                      <div
                        key={song.id}
                        className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-white truncate">{song.title}</div>
                          <div className="text-xs text-slate-400 truncate">
                            {[song.artistName, song.albumName, song.genre].filter(Boolean).join(' • ') || 'No extra metadata yet'}
                          </div>
                          {song.notes ? (
                            <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{song.notes}</div>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            className="text-[11px] px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15"
                            onClick={() =>
                              setSongForm({
                                id: song.id,
                                title: song.title || '',
                                artistName: song.artistName || '',
                                albumName: song.albumName || '',
                                genre: song.genre || '',
                                durationSeconds: song.durationSeconds || '',
                                notes: song.notes || '',
                              })
                            }
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-xl bg-white/10 hover:bg-rose-500/30"
                            onClick={() => deleteSong(song.id)}
                            title="Delete song"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-400">No songs added yet.</div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">Live Music Sets</div>
                    <div className="text-xs text-slate-400">Organise songs into show-ready sets for different venues or nights.</div>
                  </div>
                  <button
                    type="button"
                    className="rounded-xl px-3 py-2 text-xs bg-white/10 hover:bg-white/15"
                    onClick={() => {
                      setSelectedSetId(null);
                      setSelectedSetSiteId(null);
                      setSetEditor(createEmptySetForm());
                    }}
                  >
                    New Set
                  </button>
                </div>

                <div className="grid gap-3">
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Set Name</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={setEditor.name}
                      onChange={(e) => setSetEditor({ ...setEditor, name: e.target.value })}
                    />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Description</span>
                    <textarea
                      rows={3}
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={setEditor.description}
                      onChange={(e) => setSetEditor({ ...setEditor, description: e.target.value })}
                    />
                  </label>
                  <label className="text-sm text-slate-200 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!setEditor.isActive}
                      onChange={(e) => setSetEditor({ ...setEditor, isActive: e.target.checked })}
                    />
                    Active set
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
                        stageLocked ? 'bg-slate-700/60 text-slate-300 cursor-not-allowed' : 'bg-emerald-600/90 hover:bg-emerald-600'
                      }`}
                      onClick={saveSet}
                      disabled={stageLocked}
                    >
                      <Save className="w-4 h-4" /> {setEditor.id ? 'Save Set' : 'Create Set'}
                    </button>
                    {setEditor.id ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs bg-rose-600/80 hover:bg-rose-600"
                        onClick={() => deleteSet(setEditor.id)}
                      >
                        <Trash2 className="w-4 h-4" /> Delete Set
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="grid gap-2 max-h-[220px] overflow-auto">
                  {sets.length ? (
                    sets.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setSelectedSetId(Number(item.id));
                          setSelectedSetSiteId(Number(siteId));
                        }}
                        className={`text-left rounded-2xl p-3 ring-1 transition ${
                          Number(selectedSetId) === Number(item.id)
                            ? 'bg-white/12 ring-sky-400/60'
                            : 'bg-white/5 ring-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold text-white">{item.name}</div>
                          <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-lg ${
                            item.isActive ? 'bg-emerald-500/20 text-emerald-100' : 'bg-slate-500/20 text-slate-200'
                          }`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                          {item.songCount || 0} songs programmed
                        </div>
                        {item.description ? (
                          <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{item.description}</div>
                        ) : null}
                      </button>
                    ))
                  ) : (
                    <div className="text-sm text-slate-400">No sets created yet.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">Set Programming</div>
                  <div className="text-xs text-slate-400">
                    {setEditor.id
                      ? 'Choose which songs belong to this set, then mark where they can appear.'
                      : 'Create or select a set before programming songs.'}
                  </div>
                </div>
                {detailLoading ? <div className="text-xs text-slate-400">Loading set…</div> : null}
              </div>

              {setEditor.id ? (
                <>
                  <div className="grid lg:grid-cols-[1.05fr,1.1fr] gap-4">
                    <div className="grid gap-2 max-h-[420px] overflow-auto pr-1">
                      {songs.length ? songs.map((song) => {
                        const assigned = assignedSongIds.has(Number(song.id));
                        return (
                          <label
                            key={song.id}
                            className={`rounded-2xl p-3 flex items-start gap-3 ring-1 ${
                              assigned ? 'bg-sky-500/10 ring-sky-400/30' : 'bg-white/5 ring-white/10'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={assigned}
                              onChange={(e) => toggleSongInSet(song.id, e.target.checked)}
                            />
                            <span className="min-w-0">
                              <span className="block font-semibold text-white truncate">{song.title}</span>
                              <span className="block text-xs text-slate-400 truncate">
                                {[song.artistName, song.albumName].filter(Boolean).join(' • ') || 'No artist metadata'}
                              </span>
                            </span>
                          </label>
                        );
                      }) : (
                        <div className="text-sm text-slate-400">Add songs to the library first.</div>
                      )}
                    </div>

                    <div className="grid gap-3 max-h-[420px] overflow-auto pr-1">
                      {selectedSongRows.length ? selectedSongRows.map(({ assigned, song }) => (
                        <div key={song.id} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 grid gap-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold text-white truncate">{song.title}</div>
                              <div className="text-xs text-slate-400 truncate">
                                {[song.artistName, song.albumName, song.genre].filter(Boolean).join(' • ') || 'No extra metadata'}
                              </div>
                            </div>
                            <label className="grid gap-1 text-xs text-slate-300 w-20">
                              <span>Order</span>
                              <input
                                type="number"
                                min={1}
                                className="rounded-lg bg-white/5 ring-1 ring-white/10 px-2 py-1 text-slate-100"
                                value={assigned.positionValue}
                                onChange={(e) => updateSetSong(song.id, { positionValue: Number(e.target.value) || 1 })}
                              />
                            </label>
                          </div>

                          <div className="grid sm:grid-cols-3 gap-2 text-xs">
                            <label className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 flex items-center gap-2 text-slate-200">
                              <Mic2 className="w-3.5 h-3.5 text-sky-200" />
                              <input
                                type="checkbox"
                                checked={assigned.nameThatTuneEnabled}
                                onChange={(e) => updateSetSong(song.id, { nameThatTuneEnabled: e.target.checked })}
                              />
                              Name That Tune
                            </label>
                            <label className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 flex items-center gap-2 text-slate-200">
                              <ListMusic className="w-3.5 h-3.5 text-emerald-200" />
                              <input
                                type="checkbox"
                                checked={assigned.songRequestsEnabled}
                                onChange={(e) => updateSetSong(song.id, { songRequestsEnabled: e.target.checked })}
                              />
                              Song Requests
                            </label>
                            <label className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 flex items-center gap-2 text-slate-200">
                              <ShieldBan className="w-3.5 h-3.5 text-rose-200" />
                              <input
                                type="checkbox"
                                checked={assigned.songSinBinEnabled}
                                onChange={(e) => updateSetSong(song.id, { songSinBinEnabled: e.target.checked })}
                              />
                              Song Sin Bin
                            </label>
                          </div>

                          <label className="grid gap-1 text-sm">
                            <span className="text-xs text-slate-300">Set-specific notes</span>
                            <input
                              className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                              value={assigned.notes || ''}
                              onChange={(e) => updateSetSong(song.id, { notes: e.target.value })}
                            />
                          </label>
                        </div>
                      )) : (
                        <div className="text-sm text-slate-400">No songs selected for this set yet.</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 ring-1 ring-white/10 p-3">
                    <div className="text-xs text-slate-400">
                      {selectedSongRows.length} song{selectedSongRows.length === 1 ? '' : 's'} currently programmed into this set.
                    </div>
                    <button
                      type="button"
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
                        stageLocked ? 'bg-slate-700/60 text-slate-300 cursor-not-allowed' : 'bg-sky-600/90 hover:bg-sky-600'
                      }`}
                      onClick={saveSet}
                      disabled={stageLocked}
                    >
                      <Save className="w-4 h-4" /> Save Programming
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-400">
                  Select an existing set or create a new one before assigning songs and experience flags.
                </div>
              )}
            </div>
          </>
        )}

        {message ? (
          <div className="text-sm text-emerald-300 bg-emerald-900/30 border border-emerald-700/50 px-3 py-2 rounded">
            {message}
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
